import { useEffect, useState } from "react"
import { useLoading } from "@/hooks/useLoading"
import { useToast } from "@/contexts/ToastContext"
import {
  getMentores,
  getMentorias,
  getPendingMentoringsRate,
  mentoringRate,
  PendingMentoringsRateResponse,
} from "@/services/mentoriasService"
import { AgendamentoStatus, MentoresType, MentoriaDataType } from "@/types/mentorias"
import { MembroDataType } from "@/types/membros"

export function useMentoringsPage(user: MembroDataType) {
  const { showLoading, hideLoading } = useLoading()
  const toast = useToast()

  const [mentores, setMentores] = useState<MentoresType[]>([])
  const [mentoresForbidden, setMentoresForbidden] = useState(false)
  const [agendamento, setAgendamento] = useState<AgendamentoStatus | null>(null)
  const [allMentorings, setAllMentorings] = useState<MentoriaDataType[]>([])
  const [pendingMentoring, setPendingMentoring] = useState<PendingMentoringsRateResponse>()
  const [disabledAlert, setDisabledAlert] = useState(false)

  const [isScheduledOpen, setIsScheduledOpen] = useState(true)
  const [highlightedMentoringId, setHighlightedMentoringId] = useState<number | null>(null)

  const [isMentoringModalOpen, setIsMentoringModalOpen] = useState(false)
  const [selectedCalLink, setSelectedCalLink] = useState("")

  const [isMentoringFeedbackModalOpen, setIsMentoringFeedbackModalOpen] = useState(false)
  const [isHistoryFeedbackModalOpen, setIsHistoryFeedbackModalOpen] = useState(false)
  const [selectedMentoringForHistoryFeedback, setSelectedMentoringForHistoryFeedback] =
    useState<MentoriaDataType | null>(null)

  const fetchAllMentorings = async () => {
    if (!user) return []
    try {
      const response = await getMentorias(user.empId)
      setAllMentorings(response.data.data)
      return response.data.data
    } catch (error) {
      console.error("Erro ao buscar todas as mentorias:", error)
      return []
    }
  }

  useEffect(() => {
    showLoading()
    getPendingMentoringsRate(user.empId)
      .then(({ data }) => {
        if (data.data && data.data.length > 0) {
          setPendingMentoring(data.data[0])
          setDisabledAlert(true)

          if (localStorage.getItem("mentoringPendingModal") !== "false") {
            setIsMentoringFeedbackModalOpen(true)
            localStorage.setItem("mentoringPendingModal", "false")
          }
        }
      })
      .catch((error) => {
        console.error(error)
      })

    getMentores(user.empId)
      .then(({ data }) => {
        setMentores(data.data)
        setMentoresForbidden(false)
        setAgendamento(data.agendamento ?? null)
        fetchAllMentorings()
      })
      .catch((error) => {
        if (error?.response?.status === 403) {
          setMentoresForbidden(true)
        } else {
          console.error("Error fetching mentors:", error)
        }
      })
      .finally(() => {
        hideLoading()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const openBookingModal = (calLink: string) => {
    setSelectedCalLink(calLink)
    setIsMentoringModalOpen(true)
  }

  const closeBookingModal = () => {
    setIsMentoringModalOpen(false)
  }

  const handleBookingSuccess = async () => {
    closeBookingModal()
    const mentorings = await fetchAllMentorings()
    if (mentorings && mentorings.length > 0) {
      const newMentoring = mentorings.reduce((latest, current) =>
        latest.mentoria_id > current.mentoria_id ? latest : current
      )
      setIsScheduledOpen(true)
      setHighlightedMentoringId(newMentoring.mentoria_id)
      setTimeout(() => setHighlightedMentoringId(null), 5000)
    }
  }

  const openHistoryFeedbackModal = (mentoria: MentoriaDataType) => {
    setSelectedMentoringForHistoryFeedback(mentoria)
    setIsHistoryFeedbackModalOpen(true)
  }

  const closeHistoryFeedbackModal = () => {
    setSelectedMentoringForHistoryFeedback(null)
    setIsHistoryFeedbackModalOpen(false)
  }

  const submitHistoryFeedback = (rating: number, comments: string) => {
    if (!selectedMentoringForHistoryFeedback) return

    showLoading()
    mentoringRate({
      mentoria_id: selectedMentoringForHistoryFeedback.mentoria_id,
      emp_id: user.empId,
      mentorado_id: user.pesId,
      mentorado_avaliacao: rating,
      mentorado_observacao: comments,
    })
      .then(() => {
        toast.success("Avaliação enviada com sucesso!")
        setIsHistoryFeedbackModalOpen(false)
        fetchAllMentorings()
      })
      .catch(() => {
        toast.danger("Erro ao enviar avaliação.")
      })
      .finally(() => {
        hideLoading()
      })
  }

  const openPendingFeedbackModal = () => {
    setIsMentoringFeedbackModalOpen(true)
  }

  const closePendingFeedbackModal = () => {
    setIsMentoringFeedbackModalOpen(false)
    setDisabledAlert(true)
  }

  const submitPendingFeedback = (rating: number, comments: string) => {
    if (!pendingMentoring) return
    showLoading()
    mentoringRate({
      mentoria_id: pendingMentoring.mentoria_id,
      emp_id: user.empId,
      mentorado_id: user.pesId,
      mentorado_avaliacao: rating,
      mentorado_observacao: comments,
    })
      .then(() => {
        toast.success("Avaliação enviada com sucesso!")
        setIsMentoringFeedbackModalOpen(false)
        setPendingMentoring(undefined)
        setDisabledAlert(false)
      })
      .catch(() => {
        toast.danger("Erro ao enviar avaliação.")
      })
      .finally(() => {
        hideLoading()
      })
  }

  return {
    mentores,
    mentoresForbidden,
    agendamento,
    allMentorings,
    pendingMentoring,
    disabledAlert,
    isScheduledOpen,
    setIsScheduledOpen,
    highlightedMentoringId,
    isMentoringModalOpen,
    selectedCalLink,
    openBookingModal,
    closeBookingModal,
    handleBookingSuccess,
    isMentoringFeedbackModalOpen,
    openPendingFeedbackModal,
    closePendingFeedbackModal,
    submitPendingFeedback,
    isHistoryFeedbackModalOpen,
    selectedMentoringForHistoryFeedback,
    openHistoryFeedbackModal,
    closeHistoryFeedbackModal,
    submitHistoryFeedback,
  }
}
